
import {
  computed, defineComponent, onMounted, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import ProgressSpinner from 'primevue/progressspinner';
import Gleap from 'gleap';
import { fetchPlansTiers } from '@/composables/billing/billingApi';
import { PlansTiersResponse } from '@/types/billing-types';
import TierAutoUpgradeBlock from '@/components/billing/panels/TierAutoUpgradeBlock.vue';

interface DisplayedPlan {
  code: string;
  name: string;
  tagline: string;
  /** Catalog monthly price. Only price displayed from the front — yearly
   *  subscriptions are quote-only (Gleap support), no client-side multiplier. */
  priceMonthly: number;
  features: string[];
  isCurrent: boolean;
  canResubscribe: boolean;
  /** True for plans without a public price (e.g. "Pro" V5) : the card
   *  displays "Nous contacter" instead of a price, and the CTA opens the
   *  support channel (Gleap) instead of moving to the next phase. */
  isContactSales: boolean;
  /** Position of this plan vs the user's current plan. `null` when the
   *  current plan is unknown (no active subscription, contact-sales plan). */
  relation: 'current' | 'upgrade' | 'downgrade' | null;
}

// Hierarchical ordering used to derive upgrade/downgrade vs the current plan.
// Keep `pro` at the top so the V5 contact-sales card is treated as the
// strongest upgrade path.
const PLAN_RANK: Record<string, number> = {
  standard: 1,
  premium: 2,
  ultimate: 3,
  pro: 4,
};

// Plan feature lists differ between V4 and V5 — even Standard and Premium,
// which exist in both, don't expose the same features. V4 reflects the legacy
// MyOffer14 catalog (extracted from configs/offers.ts, visible features only;
// API was hidden in legacy). V5 starts as a copy of V4 and adds Ultimate/Pro;
// product will refine V5 once the V5 marketing list is finalised.
//
// Each i18n key resolves under `billing.compare.feature.{v4|v5}.{key}`.
type OfferVersionKey = 'v4' | 'v5';
const PLAN_FEATURE_KEYS_BY_VERSION: Record<OfferVersionKey, Record<string, string[]>> = {
  v4: {
    standard: [
      'unlimitedEmails',
      'automation',
      'popins',
      'sms',
      'pushWeb',
      'listSegment',
      'multichannelRecommendation',
      'emailStats',
      'support',
    ],
    premium: [
      'standardPlus',
      'campaignsPush',
      'multiAccount',
      'export',
      'deliverabilityStats',
      'statBuilder',
      'appointmentCS',
      'telephoneSupport',
    ],
    // V4 backend never returns Ultimate / Pro — entries omitted on purpose.
  },
  v5: {
    standard: [
      'pos1',
      'user1',
      'customData2',
      'allChannels',
      'conditionalBlocks',
      'essentialStats',
      'helpcenter',
    ],
    premium: [
      'pos5',
      'users10',
      'customData10',
      'statBuilderFull',
      'exportData',
      'apiAccess',
      'preferentialMsgRates',
      'phoneSupport',
      'standardIncluded',
    ],
    ultimate: [
      'pos50',
      'usersUnlimited',
      'customData100',
      'premiumIncluded',
    ],
    pro: [
      'posUnlimited',
      'usersUnlimited',
      'customDataUnlimited',
      'ultimateIncluded',
      'customQuote',
    ],
  },
};

export default defineComponent({
  name: 'PlanComparator',

  components: {
    Button, Dropdown, ProgressSpinner, TierAutoUpgradeBlock,
  },

  props: {
    modelValue: {
      type: String as PropType<string | null>,
      default: null,
    },

    frequency: {
      type: String as PropType<'monthly' | 'yearly'>,
      default: 'monthly',
    },

    currentPlanCode: {
      type: String as PropType<string | null>,
      default: null,
    },

    /**
     * Force the comparator to load and render a different offer grid than the
     * shop's natural one. Only `'v5'` is supported today — used by
     * MigrateV5Dialog to let a V4 shop browse the V5 catalog.
     */
    offerOverride: {
      type: String as PropType<'v4' | 'v5' | null>,
      default: null,
    },

    /**
     * When true, the comparator switches to "migration" UX :
     *   - shows the {@link currentOfferBanner} above the tier selector,
     *   - hides the "current plan" pill on each plan card,
     *   - replaces upgrade/downgrade CTAs with a single « Migrer vers {plan} ».
     */
    migrationMode: {
      type: Boolean,
      default: false,
    },

    /**
     * Banner content displayed at the top of the comparator when
     * {@link migrationMode} is on. Lets the user see their current offer
     * (plan + monthly price) without it interfering with the new V5 grid.
     * `priceLabel` is optional: V3 legacy bundles have no comparable
     * monthly price, the banner then shows the offer name alone.
     */
    currentOfferBanner: {
      type: Object as PropType<{
        planName: string;
        priceLabel?: string;
        subtitle?: string;
      } | null>,

      default: null,
    },

    /**
     * V5 auto-tier-upgrade configuration. Two-way bound via the matching
     * emits below. Ignored in V4 (the block is hidden anyway). Defaults are
     * computed inside {@link TierAutoUpgradeBlock} so the parent can pass
     * `undefined` and let the child take care of `tierAutoUpgradeMax` init.
     */
    tierAutoUpgradeEnabled: {
      type: Boolean,
      default: true,
    },

    tierAutoUpgradeMax: {
      type: Number,
      default: 0,
    },

    /**
     * Shop-level active discount on the plan price (`discount.plan` in
     * `shops_plans.plan` JSON, surfaced as `overview.plan.discountPercent`).
     * When truthy and in the open interval (0, 100), each plan card shows
     * both the struck-through catalog price AND the discounted price plus
     * a "-X%" pill — same percent applied to all 3 plans so the
     * comparison stays apples-to-apples. Null / 0 / 100 ⇒ no discount UI.
     */
    discountPercent: {
      type: Number as PropType<number | null>,
      default: null,
    },
  },

  emits: [
    'update:modelValue',
    'update:frequency',
    'update:targetTier',
    'update:tierAutoUpgradeEnabled',
    'update:tierAutoUpgradeMax',
    'confirm',
    'error',
  ],

  setup(props, { emit }) {
    const { t } = useI18n();
    const tiers = ref<PlansTiersResponse | null>(null);
    const loading = ref(false);
    const localFrequency = ref<'monthly' | 'yearly'>(props.frequency);
    const selectedTier = ref<number | null>(null);

    const loadTiers = async () => {
      loading.value = true;
      try {
        const result = await fetchPlansTiers(
          props.offerOverride ? { overrideOffer: props.offerOverride } : {},
        );
        // Structured error from the back (e.g. PAGES_VIEWED_UNAVAILABLE for a
        // V4 shop whose page views are not yet computed) — forward the code
        // to the parent dialog so it can switch to a "Contact support" state
        // instead of leaving the modal empty. A `null` result (empty or
        // malformed response) is surfaced the same way: without the emit the
        // comparator renders nothing and the wizard body stays blank.
        if (!result || 'error' in result) {
          emit('error', (result && 'error' in result ? result.error.code : null) ?? 'UNKNOWN');
          tiers.value = null;
          return;
        }
        tiers.value = result as PlansTiersResponse | null;
        if (tiers.value) {
          // Default tier = the shop's natural tier (minimum tier covering the
          // current metric — contacts for V5, pages views for V4 -- back side).
          const naturalTier = tiers.value.tiers.find((t) => t.isCurrent);
          selectedTier.value = naturalTier
            ? naturalTier.threshold
            : tiers.value.tiers[0]?.threshold ?? null;
          emit('update:targetTier', selectedTier.value ?? undefined);
        }
      } finally {
        loading.value = false;
      }
    };

    /**
     * Tier minimum the shop is allowed to subscribe to — couvering its
     * current contacts count. Tiers below this threshold are hidden from the
     * dropdown (spec V5-d): the customer can never downgrade to a tier that
     * would not cover their current usage.
     */
    const minSelectableTier = computed<number | null>(() => (
      tiers.value?.currentMetric?.tierUpperBound ?? null
    ));

    const setFrequency = (f: 'monthly' | 'yearly') => {
      localFrequency.value = f;
      emit('update:frequency', f);
    };

    const currentTier = computed(() => {
      if (!tiers.value || selectedTier.value === null) return null;
      return tiers.value.tiers.find((t) => t.threshold === selectedTier.value) ?? null;
    });

    const tierOptions = computed(() => {
      if (!tiers.value) return [];
      // Hide tiers below the shop's current usage (spec V5-d): the customer
      // sees only its natural tier + every higher tier. `minSelectableTier`
      // is null in V4 (selectable=false), in which case we don't render the
      // dropdown at all, so the filter is a no-op.
      const min = minSelectableTier.value;
      return tiers.value.tiers
        .filter((tier) => min == null || tier.threshold >= min)
        .map((tier) => ({
          value: tier.threshold,
          label: t('billing.compare.tier.option', { count: formatNumber(tier.threshold) }),
        }));
    });

    // Guard: if for any reason `selectedTier` ends up below `minSelectableTier`
    // (legacy state, prop override, etc.), snap it up to the natural tier so
    // the dropdown never displays a value it doesn't propose.
    watch([minSelectableTier, selectedTier], ([min, current]) => {
      if (min == null || current == null) return;
      if (current < min) {
        selectedTier.value = min;
        emit('update:targetTier', min);
      }
    });

    const visiblePlans = computed<DisplayedPlan[]>(() => {
      if (!currentTier.value || !tiers.value) return [];
      const version: OfferVersionKey = tiers.value.pricingModel === 'contacts' ? 'v5' : 'v4';
      const versionCatalog = PLAN_FEATURE_KEYS_BY_VERSION[version];

      // Migration mode lives on a *different* offer grid than the user's
      // current plan — the V4 "premium" code happens to match V5 "premium"
      // but the underlying offer is different, so the current/upgrade/
      // downgrade relation is meaningless here.
      const currentRank = !props.migrationMode && props.currentPlanCode
        ? PLAN_RANK[props.currentPlanCode] ?? null
        : null;

      const plans: DisplayedPlan[] = [];
      for (const [code, price] of Object.entries(currentTier.value.prices)) {
        const priceMonthly = Number(price);
        const featureKeys = versionCatalog[code] ?? [];
        const isCurrent = !props.migrationMode && props.currentPlanCode === code;
        let relation: DisplayedPlan['relation'] = null;
        if (isCurrent) {
          relation = 'current';
        } else if (currentRank !== null) {
          const planRankValue = PLAN_RANK[code];
          if (planRankValue !== undefined) {
            relation = planRankValue > currentRank ? 'upgrade' : 'downgrade';
          }
        }
        plans.push({
          code,
          name: t(`billing.compare.planName.${code}`),
          tagline: t(`billing.compare.planTagline.${code}`),
          priceMonthly,
          features: featureKeys.map((key) => t(`billing.compare.feature.${version}.${key}`)),
          isCurrent,
          canResubscribe: false,
          isContactSales: false,
          relation,
        });
      }

      // V5 offer : always surface the "Pro" plan as "contact sales", even if
      // it is absent from the public pricing grid. Pro pricing is custom and
      // handled outside the standard subscription flow.
      const hasPro = plans.some((p) => p.code === 'pro');
      if (version === 'v5' && !hasPro) {
        plans.push({
          code: 'pro',
          name: t('billing.compare.planName.pro'),
          tagline: t('billing.compare.planTagline.pro'),
          priceMonthly: 0,
          features: (versionCatalog.pro ?? []).map(
            (key) => t(`billing.compare.feature.${version}.${key}`),
          ),
          isCurrent: !props.migrationMode && props.currentPlanCode === 'pro',
          canResubscribe: false,
          isContactSales: true,
          // Pro plan is contact-sales; relation isn't meaningful for the badge.
          relation: null,
        });
      }

      return plans;
    });

    // Recommended plan: Premium (best balance value/price). Highlighted
    // regardless of V4 vs V5 — only displayed if Premium exists in the grid.
    // Never recommend a contact-sales plan. Hidden once the shop already has
    // an active plan : the "current plan" ribbon carries the more relevant
    // context. Kept visible in migration mode (no « current plan » badge
    // there since the user lives on a different offer grid).
    const recommendedPlan = computed(() => {
      if (props.currentPlanCode && !props.migrationMode) return null;
      const codes = visiblePlans.value
        .filter((p) => !p.isContactSales)
        .map((p) => p.code);
      return codes.includes('premium') ? 'premium' : null;
    });

    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);
    const formatPrice = (n: number) => {
      if (n === 0) return t('billing.compare.free');
      const symbol = tiers.value?.currency === 'USD' ? '$' : '€';
      return `${new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n)} ${symbol}`;
    };

    /**
     * Whether a usable shop-level plan discount is active. Guards against
     * degenerate values (0% or 100% would either be a no-op or render a free
     * plan, neither of which is meaningful for the comparator).
     */
    const discountActive = computed<boolean>(() => {
      const pct = props.discountPercent;
      return pct != null && Number.isFinite(pct) && pct > 0 && pct < 100;
    });

    /**
     * Returns the post-discount amount for a given catalog price, or
     * `null` when no discount is active (so the template can fall back
     * to the plain catalog display).
     */
    const discountedPriceOf = (catalogPrice: number): number | null => {
      if (!discountActive.value) return null;
      const pct = props.discountPercent as number;
      return Math.round(catalogPrice * (1 - pct / 100) * 100) / 100;
    };

    /**
     * Yearly billing combined with an active plan discount is quote-only :
     * we can't surface a simple sticker price for that combination, so every
     * priced card switches to a "Nous contacter" display (like the Entreprise
     * card) and its CTA already routes to support in yearly mode.
     */
    const isYearlyDiscountContact = computed<boolean>(() => (
      localFrequency.value === 'yearly' && discountActive.value
    ));

    /**
     * Whether the auto-tier-upgrade block should be rendered right under
     * the tier dropdown. V5-only — hidden in migration mode (no auto-tier
     * UX yet) and in yearly (no self-served subscription).
     */
    const showAutoTierBlock = computed<boolean>(() => Boolean(
      tiers.value?.selectable
      && !props.migrationMode
      && localFrequency.value === 'monthly'
      && selectedTier.value,
    ));

    // Default plafond for auto-tier-upgrade = chosen tier + 3 paliers, or
    // the grid max if fewer than 3 tiers exist above. Returns 0 when the
    // shop is already at the top (no plafond to set). Centralised here so
    // the parent receives a correct value the moment the block becomes
    // visible — TierAutoUpgradeBlock is a pure presenter.
    const DEFAULT_PALIER_OFFSET = 3;
    const computeDefaultMaxTier = (tier: number): number => {
      if (!tiers.value) return 0;
      const sortedAbove = tiers.value.tiers
        .map((t) => t.threshold)
        .filter((th) => th > tier)
        .sort((a, b) => a - b);
      if (sortedAbove.length === 0) return 0;
      return sortedAbove[Math.min(DEFAULT_PALIER_OFFSET - 1, sortedAbove.length - 1)];
    };

    // Init / repair maxTier whenever the auto-tier block becomes visible or
    // the chosen tier changes. The parent owns the value (v-model); the
    // child block never self-initialises — this avoids the previous race
    // where the child emitted before the parent had wired the listener.
    watch(
      [showAutoTierBlock, selectedTier, () => tiers.value?.tiers.length ?? 0],
      ([visible, tier]) => {
        if (!visible || !tier) return;
        const valid = props.tierAutoUpgradeMax > tier
          && (tiers.value?.tiers.some((t) => t.threshold === props.tierAutoUpgradeMax) ?? false);
        if (valid) return;
        const def = computeDefaultMaxTier(tier);
        if (def !== props.tierAutoUpgradeMax) {
          emit('update:tierAutoUpgradeMax', def);
        }
      },
      { immediate: true },
    );

    /**
     * `true` when the user has selected a tier strictly greater than the
     * shop's natural tier (= more contacts than currently used). In V5, on
     * the *current* plan, this is the « modify tier only » path : same plan
     * but bigger capacity, charged at prorata. Cf. spec V5-i.
     */
    const isUpgradingTierOnly = computed<boolean>(() => {
      const min = minSelectableTier.value;
      if (min == null || selectedTier.value == null) return false;
      return selectedTier.value > min;
    });

    /**
     * The CTA label is contextual to the user's current situation:
     *   - On the same plan, tier inchangé → « Offre actuelle » (button disabled).
     *   - On the same plan, tier supérieur → « Modifier mon palier » (paid prorata).
     *   - Pro / contact-sales              → « Nous contacter ».
     *   - Upgrade vs current plan          → « Passer à {plan} » (immediate paid switch).
     *   - Downgrade vs current             → « Programmer {plan} » (effective next renewal).
     *   - No current plan at all           → « Souscrire à {plan} » (first subscription).
     */
    const ctaLabel = (plan: DisplayedPlan) => {
      if (plan.isContactSales) return t('billing.compare.cta.contact');
      // Yearly plans are quote-only — route every CTA to Gleap support.
      // No prorata / no self-served subscription on yearly from the front.
      if (localFrequency.value === 'yearly') return t('billing.compare.cta.contact');
      // Migration mode: every plan is a target migration choice — no current /
      // upgrade / downgrade distinction (the user's current offer lives on the
      // V4 grid, irrelevant for the V5 picker).
      if (props.migrationMode) {
        return t('billing.compare.cta.migrate', { plan: plan.name });
      }
      if (plan.isCurrent) {
        // Same plan, but the user picked a higher tier than their natural one
        // → propose to change the tier only (prorata charged like an upgrade).
        return isUpgradingTierOnly.value
          ? t('billing.compare.cta.modifyTier')
          : t('billing.compare.cta.current');
      }
      if (plan.relation === 'upgrade') {
        return t('billing.compare.cta.upgrade', { plan: plan.name });
      }
      if (plan.relation === 'downgrade') {
        return t('billing.compare.cta.downgrade', { plan: plan.name });
      }
      // No relation = user has no active plan; this is a first subscription.
      return t('billing.compare.cta.subscribe', { plan: plan.name });
    };

    /**
     * Whether the CTA button is interactive. The "current plan" CTA is normally
     * disabled, but becomes enabled when the user has bumped the tier above
     * the natural one (modify-tier-only path).
     */
    const ctaDisabled = (plan: DisplayedPlan): boolean => {
      if (plan.isContactSales) return false;
      if (plan.isCurrent && !plan.canResubscribe) {
        return !isUpgradingTierOnly.value;
      }
      return false;
    };

    /**
     * Opens the Gleap support widget. Used by "Pro" plan CTA — pricing is
     * custom and handled outside the standard flow.
     */
    const openContactSales = () => {
      try {
        Gleap.open();
      } catch {
        // Gleap not initialised — fallback to mailto on the support address.
        window.open('mailto:contact@shopimind.com?subject=Demande%20offre%20Pro', '_blank');
      }
    };

    const selectPlan = (code: string) => {
      emit('update:modelValue', code);
    };

    /**
     * Triggered by the "Choisir" CTA inside each plan card. Sets the model
     * AND immediately tells the parent to move to the next phase.
     */
    const confirmPlan = (code: string) => {
      emit('update:modelValue', code);
      emit('confirm', code);
    };

    watch(selectedTier, (v) => {
      emit('update:targetTier', v ?? undefined);
    });

    watch(() => props.frequency, (v) => {
      localFrequency.value = v;
    });

    onMounted(() => loadTiers());

    return {
      t,
      tiers,
      loading,
      localFrequency,
      selectedTier,
      tierOptions,
      visiblePlans,
      recommendedPlan,
      showAutoTierBlock,
      setFrequency,
      selectPlan,
      confirmPlan,
      openContactSales,
      formatNumber,
      formatPrice,
      discountedPriceOf,
      isYearlyDiscountContact,
      ctaLabel,
      ctaDisabled,
    };
  },
});
